import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthCallback = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState('processing');

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          toast.error('No session ID found');
          navigate('/');
          return;
        }

        // Exchange session_id for user data and session_token
        const response = await fetch(`${BACKEND_URL}/api/auth/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-ID': sessionId
          }
        });

        if (!response.ok) {
          throw new Error('Authentication failed');
        }

        const data = await response.json();

        // Store session token
        localStorage.setItem('session_token', data.session_token);
        localStorage.setItem('user', JSON.stringify(data.user));

        setStatus('success');
        toast.success(t('login_successful'));
        
        // Clean URL and redirect
        window.history.replaceState(null, '', '/dashboard');
        setTimeout(() => {
          navigate('/dashboard');
        }, 500);
      } catch (error) {
        console.error('Auth callback error:', error);
        setStatus('error');
        toast.error(t('error_occurred'));
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    };

    processCallback();
  }, [navigate, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-teal-50 to-blue-50">
      <div className="glass-card p-8 rounded-2xl text-center max-w-md">
        {status === 'processing' && (
          <>
            <div className="loading-spinner mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('authenticating')}</h2>
            <p className="text-gray-600">{t('please_wait')}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#e8f9ed'}}>
              <svg className="w-8 h-8" style={{color: '#36e961'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('success')}</h2>
            <p className="text-gray-600">{t('redirecting')}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('error_occurred')}</h2>
            <p className="text-gray-600">{t('back_to_home')}</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
